// @ts-nocheck
import { switchMap, first, concatMap, map } from "rxjs/operators";
import { setState, state } from "../";
import { logOutObservable } from "../../services/Auth/AuthService";
import {
  Challenge,
  DurationMapping,
  ScheduleMapping,
} from "../../services/Gamification/interfaces/Challenge.interface";
import {
  addUserChallenge,
  addUserQuizz,
  bulkUpdateActionStatus,
  cancelPendingChallengeNotifications,
  deleteUserProfile,
  generateActions,
  getChallengeNotificationId,
  getUserChallengeById,
  getUserProfile,
  updateActionStatus,
  updateUserChallenge,
  updateUserFeedItemObservable,
  updateUserProfile,
  userProfileObservable,
} from "../../services/User/User";
import {
  ActionStatus,
  FeedItemType,
  ScheduledAction,
  TutorialPopover,
  UserChallenge,
  UserProfile,
} from "../../services/User/User.interface";
import { newUserDateTime } from "../../utils/date";
import { CustomError } from "../../utils/error";
import { logDebug, logger } from "../../utils/logger";
import { reportError } from "../error/effects";
import { defaultUserState } from "./state";
import {
  actionsByListIdsObservable,
  challengeByIdObservable,
  tutorialsObservable,
} from "../../services/Gamification/Gamification";
import { NotificationSchedule } from "../../services/Device/Device.interface";
import { NotificationsManager } from "../../services/Device/notifications";
import { add } from "date-fns";
import { combineLatest, from, forkJoin, catchError, EMPTY } from "rxjs";
import NotificationsData from "../../content/app/data/app_notifications.json";
import { LocalTranslator } from "../../utils/i8n";
import { replace as Rreplace, pipe as Rpipe } from "ramda";

type SuccessHandler = () => void;

function updateAuthState(user: UserProfile) {
  const effect = "updateAuthState";
  setState("USER", { USER: user }, effect);
}

function newChallenge(
  challenge: Challenge,
  chapterId: string,
  startDateTime: {
    year: number;
    date: number;
    month: number;
    hour: number;
    minute: number;
  }
): UserChallenge {
  const challengeStartDateTime = newUserDateTime({
    ...startDateTime,
    second: 0,
  });

  const localizations = challenge.localizations.reduce(
    (acc, lc) => {
      return { ...acc, [lc.locale]: lc.id };
    },
    { [challenge.locale]: challenge.id }
  );

  return {
    scheduledActions: generateActions({
      actions: challenge.actions,
      challengeId: challenge.id,
      chapterId: chapterId,
      duration: DurationMapping[challenge.duration.toLowerCase()],
      schedule: ScheduleMapping[challenge.schedule.toLowerCase()],
      startDate: challengeStartDateTime.getTime(),
    }),
    localizations,
    challengeTitle: challenge.title,
    completion: 0,
    challengeId: challenge.id,
    schedule: challenge.schedule,
    challengeStatus: "ONGOING",
    createdAt: new Date().getTime(),
    updatedAt: new Date().getTime(),
    startDate: challengeStartDateTime.getTime(),
    reminderTime: {
      hour: startDateTime.hour,
      minutes: startDateTime.minute,
    },
  };
}

function scheduleChallengeNotifications(
  challenge: Challenge,
  scheduledActions: Array<ScheduledAction>,
  notificationTime: { hour: number; minute: number }
) {
  const today = new Date(Date.now());

  const upcomingActions = scheduledActions.filter(
    (ac) =>
      add(ac.actionDate, {
        hours: notificationTime.hour,
        minutes: notificationTime.minute,
      }).getTime() > today.getTime()
  );

  const actions$ = actionsByListIdsObservable(
    upcomingActions.map((ac) => ac.actionId),
    state.DEVICE.languageCode
  );

  return actions$.pipe(
    switchMap((actions) =>
      from(actions).pipe(
        switchMap((action) => {
          const actionsToSchedule = upcomingActions.filter(
            (ac) => ac.actionId === action.id
          );
          return from(actionsToSchedule).pipe(
            concatMap((scheduledAction) => {
              const notificationDay = new Date(scheduledAction.actionDate);
              const notificationId = getChallengeNotificationId(
                challenge.id,
                scheduledAction.actionDate
              );
              const schedule: NotificationSchedule = {
                year: notificationDay.getFullYear(),
                month: notificationDay.getMonth(),
                date: notificationDay.getDate(),
                hour: notificationTime.hour,
                minute: notificationTime.minute,
                second: 0,
              };

              const notificationLabels: typeof NotificationsData.EN =
                LocalTranslator(state.DEVICE.languageCode)(NotificationsData);

              const title = Rreplace(
                "{0}",
                challenge.title,
                notificationLabels.action_title
              );
              //TODO: Add two digits number on the notifications
              const body = Rpipe(
                Rreplace("{0}", notificationTime.hour.toString()),
                Rreplace("{1}", notificationTime.minute.toString()
                  .padStart(2, "0")),
                Rreplace("{2}", action.title)
              )(notificationLabels.action_text);

              return new NotificationsManager().scheduleNotification({
                notificationId,
                schedule,
                title,
                body,
              });
            })
          );
        })
      )
    )
  );
}

function addStartedChallengeObservble(startedChallenge: UserChallenge) {
  return addUserChallenge(state.USER.userId, startedChallenge).pipe(
    concatMap(() =>
      updateUserProfile(state.USER.userId, {
        stats: {
          ...state.USER.stats,
          challenges: {
            ...state.USER.stats.challenges,
            count: state.USER.stats.challenges.count + 1,
          },
        },
        updatedAt: new Date().getTime(),
      })
    )
  );
}

function scheduleNewChallengeNotifications(
  challenge: Challenge,
  scheduledActions: Array<ScheduledAction>,
  hour: number,
  minute: number
) {
  return scheduleChallengeNotifications(challenge, scheduledActions, {
    hour,
    minute,
  }).pipe(
    catchError((err: Error) => {
      console.log(err);
      if (
        err.message.startsWith("Notifications not supported in this browser")
      ) {
        logger.error(
          `Notifications are not enabled on this platform: ${JSON.stringify(
            state.DEVICE
          )}`
        );
        return EMPTY;
      }
      throw err;
    })
  );
}

function startUserChallenge({
  challengeId,
  chapterId,
  startDateTime,
}: {
  challengeId: string;
  chapterId: string;
  startDateTime: {
    year: number;
    date: number;
    month: number;
    hour: number;
    minute: number;
  };
}) {
  return challengeByIdObservable(challengeId).pipe(
    switchMap((challenge) => {
      const newUserChallenge = newChallenge(
        challenge,
        chapterId,
        startDateTime
      );
      return forkJoin([
        addStartedChallengeObservble(newUserChallenge),
        scheduleNewChallengeNotifications(
          challenge,
          newUserChallenge.scheduledActions,
          startDateTime.hour,
          startDateTime.minute
        ),
      ]);
    })
  );
}

function startNewChallengeEffect({
  challengeId,
  chapterId,
  startDateTime,
}: {
  challengeId: string;
  chapterId: string;
  startDateTime: {
    year: number;
    date: number;
    month: number;
    hour: number;
    minute: number;
  };
}) {
  logger.info(
    "Start new challeges: ChallegneId",
    challengeId,
    "Parameterrs:",
    startDateTime
  );
  const effect = "startNewChallengeEffect";
  return startUserChallenge({
    challengeId,
    chapterId,
    startDateTime,
  }).subscribe({
    error(err) {
      reportError("USER", err, effect);
    },
  });
}

function loginFailedEffect(error: Error) {
  const effect = "loginFailed";
  const loginFailedError = new CustomError(`Login failed: ${error.message}`, {
    name: "AuthError",
    severity: "ERROR",
  });
  setState("USER", { USER: defaultUserState }, effect);
  reportError("USER", loginFailedError, effect);
}

function updateChallengeReminderTimeEffect(
  challengeId: string,
  reminderTime: { hour: number; minutes: number }
) {
  const effect = "updateChallengeReminderTimeEffect";
  return combineLatest([
    getUserChallengeById(state.USER.userId, challengeId),
    challengeByIdObservable(challengeId),
  ])
    .pipe(
      concatMap(([userChallenge, challenge]) => {
        return updateUserChallenge(
          state.USER.userId,
          userChallenge.challengeId,
          {
            reminderTime,
          }
        ).pipe(
          concatMap(() =>
            cancelPendingChallengeNotifications(userChallenge.scheduledActions)
          ),
          concatMap(() =>
            scheduleChallengeNotifications(
              challenge,
              userChallenge.scheduledActions,
              { hour: reminderTime.hour, minute: reminderTime.minutes }
            )
          )
        );
      })
    )
    .subscribe({
      error(err) {
        reportError("USER", err, effect);
      },
    });
}

function bulkUpdateChallengeActionStatusEffect(
  actions: Array<{
    challengeId: string;
    toggled: boolean;
    actionDate: number;
  }>
) {
  const effect = "bulkUpdateChallengeActionStatusEffect";
  return bulkUpdateActionStatus(state.USER.userId, actions).subscribe({
    error(err) {
      reportError("USER", err, effect);
    },
  });
}

function updateChallengeActionStatusEffect(
  actionDate: number,
  challengeId: string,
  actionStatus: ActionStatus
) {
  const effect = "updateChallengeActionStatus";
  logDebug("user-effects", "updateChallengeActionStatusEffect", "params", {
    actionDate,
    challengeId,
    actionStatus,
  });
  return updateActionStatus(state.USER.userId, challengeId, [
    { actionStatus, actionDate },
  ])
    .pipe(
      concatMap(() =>
        getUserProfile(state.USER.userId).pipe(
          concatMap((userProfile) => {
            const stats = {
              ...userProfile.stats,
              actions: {
                ...userProfile.stats.actions,
                done:
                  actionStatus === "done"
                    ? userProfile.stats.actions.done + 1
                    : userProfile.stats.actions.done,
                count: userProfile.stats.actions.count + 1,
                doneStreak:
                  actionStatus === "done"
                    ? userProfile.stats.actions.doneStreak + 1
                    : 0,
                skipped:
                  actionStatus === "skipped"
                    ? userProfile.stats.actions.skipped + 1
                    : userProfile.stats.actions.skipped,
              },
            };
            logDebug(
              "user-effects",
              "updateChallengeActionStatusEffect - before profile update",
              "return",
              {
                stats,
                userProfile,
              }
            );
            return updateUserProfile(state.USER.userId, {
              stats,
              updatedAt: new Date().getTime(),
            });
          })
        )
      )
    )
    .subscribe({
      error(err) {
        reportError("USER", err, effect);
      },
    });
}

function completeUserProfileEffect(
  challengeId: string,
  chapterId: string,
  startDateTime: {
    year: number;
    date: number;
    month: number;
    hour: number;
    minute: number;
  },
  successHandler: SuccessHandler = undefined
) {
  const effect = "completeUserProfileEffect";
  return startUserChallenge({ challengeId, chapterId, startDateTime })
    .pipe(
      switchMap(() => {
        return updateUserProfile(state.USER.userId, {
          status: "completed",
          stats: {
            ...state.USER.stats,
            challenges: {
              ...state.USER.stats.challenges,
              count: state.USER.stats.challenges.count + 1,
            },
          },
          updatedAt: new Date().getTime(),
        });
      })
    )
    .subscribe({
      next() {
        if (successHandler) {
          successHandler();
        }
      },
      error(err) {
        reportError("DEVICE", err, effect);
      },
    });
}

function logOutEffect() {
  const effect = "logOutEffect";
  return logOutObservable()
    .pipe(first())
    .subscribe({
      next() {
        updateAuthState(defaultUserState);
      },
      error(err) {
        reportError("USER", err, effect);
      },
    });
}

function completeWelcomeQuestionsEffect(
  quizzId: string,
  quizzName: string,
  answers: {},
  handler: () => void = undefined
) {
  const effect = "completeWelcomeQuestionsEffect";
  return addUserQuizz(state.USER.userId, {
    name: quizzName,
    quizzId,
    result: answers,
  })
    .pipe(
      switchMap(() => {
        return updateUserProfile(state.USER.userId, {
          level: "NOVICE",
          status: "tutorial",
          updatedAt: new Date().getTime(),
        });
      })
    )
    .subscribe({
      error(err) {
        reportError("ERROR", err, effect);
      },
      next() {
        if (handler) {
          handler();
        }
      },
    });
}

function updateUserLanguageEffect(lang: string) {
  const effect = "updateUserLanguageEffect";
  console.log("Called language effect with update", lang);
  return updateUserProfile(state.USER.userId, { userLanguage: lang }).subscribe(
    {
      error(err) {
        reportError("ERROR", err, effect);
      },
    }
  );
}

function dismissFeedItemEffect(itemId: string, feedType: FeedItemType) {
  const effect = "dismissFeedItemEffect";
  return updateUserFeedItemObservable(state.USER.userId, {
    itemId,
    status: "dismissed",
    type: feedType,
    updatedAt: new Date().getTime(),
  }).subscribe({
    error(err) {
      reportError("USER", err, effect);
    },
  });
}

function updateTutorialProgressEffect(tutorialId: string) {
  const effect = "updateTutorialProgressEffect";
  if (!state.USER.tutorial.includes(tutorialId)) {
    return updateUserProfile(state.USER.userId, {
      tutorial: [...state.USER.tutorial, tutorialId],
      updatedAt: new Date().getTime(),
    }).subscribe({
      error(err) {
        reportError("USER", err, effect);
      },
    });
  } else {
    return EMPTY.subscribe();
  }
}

function deleteUserProfileEffect() {
  const effect = 'deleteUserProfileEffect';
  return deleteUserProfile(state.USER.userId).subscribe(
    {
      error(err) {
        reportError('USER', err, effect)
      }
    }
  )
}


function userTutorialEffect(locale: string) {
  const effect = "updateUserTutorialEffect";
  return tutorialsObservable(locale)
    .pipe(
      switchMap((tutorials) => {
        return userProfileObservable(state.USER.userId).pipe(
          map(({ tutorial }) => {
            const pendingTutorials = tutorials
              .filter((t) => !tutorial.includes(t.tutorialId))
              .sort((a, b) => a.tutorialId.localeCompare(b.tutorialId));
            if (pendingTutorials.length > 0) {
              const nextTutorial = pendingTutorials[0];
              const tutorialPopover: TutorialPopover = {
                buttonLabel: "Ok",
                text: nextTutorial.text,
                title: nextTutorial.tutorialTitle,
                id: nextTutorial.tutorialId,
              };
              return tutorialPopover;
            } else {
              const emptyPopover: TutorialPopover = {
                buttonLabel: "",
                text: "",
                title: "",
                id: "empty",
              };
              return emptyPopover;
            }
          })
        );
      })
    )
    .subscribe({
      next(pop) {
        setState(
          "TUTORIAL",
          {
            TUTORIAL: pop,
          },
          effect
        );
      },
    });
}

export {
  updateAuthState,
  loginFailedEffect,
  startNewChallengeEffect,
  updateChallengeReminderTimeEffect,
  updateChallengeActionStatusEffect,
  completeUserProfileEffect,
  logOutEffect,
  completeWelcomeQuestionsEffect,
  updateUserLanguageEffect,
  dismissFeedItemEffect,
  updateTutorialProgressEffect,
  userTutorialEffect,
  bulkUpdateChallengeActionStatusEffect,
  deleteUserProfileEffect
};
